import _invariant         from 'invariant';
import { createSelector } from 'reselect';
import config             from '../config';
import {
  REGISTERED_CONTENT_STATE_KEY_CONTENT_ID,
  REGISTERED_CONTENT_STATE_KEY_DRAFT_CONTENT,
  REGISTERED_CONTENT_STATE_KEY_FRIENDLY_NAME,
  REGISTERED_CONTENT_STATE_KEY_INITIAL_CONTENT,
  REGISTERED_CONTENT_STATE_KEY_IS_EDITING,
}                         from '../reducers/registeredContent';

// -----------------------------------------------------------------------------------------------------------------
// Module state selector
// -----------------------------------------------------------------------------------------------------------------

const selectEditableContentModuleState = (state) => {

  if (!state.has(config.statePath)) {

    throw new Error(
      "Unable to find module state.  Did you remember to configure the statePage?\n" +
      "See editableContent module README.md file for more info."
    );

  }

  return state.get(config.statePath);

};


// ---------------------------------------------------------------------------------------------------------------------
// inEdiMode selectors
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Selector for 'inEditMode'.
 */
export const selectInEditMode = createSelector(
  selectEditableContentModuleState,
  moduleState => moduleState.get('inEditMode')
);


// ---------------------------------------------------------------------------------------------------------------------
// registeredContent selectors
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Returns `editableContent.registeredContent` state
 */
export const selectRegisteredContent = createSelector(
  selectEditableContentModuleState,
  (moduleState) => moduleState.get('registeredContent')
);

/**
 * Returns editable content from state, filtered by the `userCapabilities` prop.
 *
 * If all editable content is required, set `userCapabilities` to '*'.
 *
 * @return {*}
 */
export const selectDraftContent = createSelector(
  selectRegisteredContent,
  (registeredContent) => registeredContent.filter(
    content => (
      content.get(REGISTERED_CONTENT_STATE_KEY_DRAFT_CONTENT)
      !==
      content.get(REGISTERED_CONTENT_STATE_KEY_INITIAL_CONTENT)
    )
  )
);

/**
 * Selects the friendly names for draft content.
 */
export const selectDraftContentNames = createSelector(
  selectDraftContent,
  unsavedData => unsavedData.map(entry => entry.get(REGISTERED_CONTENT_STATE_KEY_FRIENDLY_NAME))
);


/**
 * Selects specific registered content item.
 */
export const makeSelectRegisteredContentDetails = () => createSelector(
  (state, {componentId}) => componentId,
  selectRegisteredContent,
  (componentId, registeredContent) => {

    _invariant(componentId, 'Missing componentId');

    return registeredContent.get(componentId) || null;

  }
);


export const makeSelectEditableContentComponentContentId = () => {

  const selectRegisteredContentDetails = makeSelectRegisteredContentDetails();

  return createSelector(
    selectRegisteredContentDetails,
    (contentDetails) => contentDetails && contentDetails.get(REGISTERED_CONTENT_STATE_KEY_CONTENT_ID),
  );

};


export const makeSelectEditableContentComponentIsEditing = () => {

  const selectRegisteredContentDetails = makeSelectRegisteredContentDetails();

  return createSelector(
    selectRegisteredContentDetails,
    (contentDetails) => contentDetails && contentDetails.get(REGISTERED_CONTENT_STATE_KEY_IS_EDITING),
  );

};


export const makeSelectEditableContentComponentInitialContent = () => {

  const selectRegisteredContentDetails = makeSelectRegisteredContentDetails();

  return createSelector(
    selectRegisteredContentDetails,
    (contentDetails) => contentDetails && contentDetails.get(REGISTERED_CONTENT_STATE_KEY_INITIAL_CONTENT),
  );

};


export const makeSelectEditableContentComponentDraftContent = () => {

  const selectRegisteredContentDetails = makeSelectRegisteredContentDetails();

  return createSelector(
    selectRegisteredContentDetails,
    (contentDetails) => contentDetails && contentDetails.get(REGISTERED_CONTENT_STATE_KEY_DRAFT_CONTENT),
  );

};


export const makeSelectRegisteredEditableContentDraftContent = () => {

  const selectRegisteredContentDetails = makeSelectRegisteredContentDetails();

  return createSelector(
    selectRegisteredContentDetails,
    (contentDetails) => contentDetails.draftContent,
  );

};


// WEBPACK FOOTER //
// ./src/modules/editableContent/selectors/editableContentSelectors.js